import { map, join, pathOr, not, equals, compose, lensPath, set, when } from 'ramda';
import { applicationUseMutation } from 'src/graphqlhooks';
import {
  usePermissionByNamespaceLoan
} from 'src/queries/permission';
import { AUTHENTICATED_USER_DOCUMENT_ARTIFACTS_MINIMAL, GLOBAL_PERMISSION_TEMPLATES, LOAN_PERMISSIONS_AND_ACTIVITY, NAMESPACE_BRANCHES } from 'src/queries/useQuery';
import { KIND_LOANAPP } from 'src/services/constants';
import {
  CREATE_LOAN_PERMISSION,
  DELETE_LOAN_PERMISSION_LOAN_ID_AND_NAME,
  IGQLPermissionVariable
} from 'src/services/grapqlQueries/permissionQuery';
import { getGlobalNamespace, getNamespaceName } from 'src/services/objects/namespaces';
import { GQLQueryBuilder, QueryData } from 'src/services/grapqlQueries/queryBuilder';
import { useOneTimeFetch } from 'src/hooks/useOneTimeFetch';
import {
  AUTHENTICATED_USER_PERMISSIONS,
  NAMESPACE_PERMISSIONS_LOAN_PRODUCTS,
  USER_POD_CONFIGS,
  USER_TEAMS,
  AUTHENTICATED_USER_DOCUMENT_ACCESS_CONTROLS
} from 'src/queries/useQuery';
import { GET_LOAN_PRODUCTS_BY_NAMESPACE, GET_PERMISSION_BY_ROLE } from 'src/services/grapqlQueries/permissionQuery';
import { GET_POD_CONFIG_BY_EMAIL } from 'src/services/grapqlQueries/brokerAllocation';
import { authenticatedUser } from 'src/utilities/storage';
import useAccounts from 'src/hooks/useAccountsHook';
import { useQueryClient } from 'react-query';
import { includes } from 'ramda';
import { isNotEmpty } from 'src/utilities/hasValue';
import { USER_ONBOARD_APPLICANT } from 'src/services/constants/accounts';
import { GET_DOCUMENT_ACCESS_CONTROLS } from 'src/services/grapqlQueries/accessControls';
import { GET_BRANCHES_SHORT_BY_NAMESPACE } from 'src/services/grapqlQueries/branches';
import siteSettings from 'src/siteSetting';
import { TEAMS_LIST_BY_ACCOUNTS } from 'src/services/grapqlQueries/TeamsData';
import { GQL_VARMAPS_SHORT_BY_NAMESPACE } from 'src/services/grapqlQueries/varmaps';
import { GQL_GLOBAL_PERMISSION_TEMPLATES } from 'src/services/grapqlQueries/permissionTemplates';
import { GQL_GLOBAL_DOCUMENT_ARTIFACTS_MINIMAL } from 'src/services/grapqlQueries/documentQuery';
import { buildLabel } from 'src/utilities/naming';
import { SchemaLoanPermission } from 'src/services/types/loan_permissions';

export const useLoanPermissionsAndActivityByLoan = (
  loanAppId: string,
  accountId: string,
  resources: string[]) => {
  const variables: IGQLPermissionVariable = {
    objectMeta: {
      namespace: getNamespaceName(),
      owner_references: [{ uid: loanAppId }]
    },
    permissionObject: {
      namespace: getNamespaceName(),
      account: accountId
    },
    loanAppIds: [loanAppId],
    resources,
  }
  const querykey = LOAN_PERMISSIONS_AND_ACTIVITY;
  return usePermissionByNamespaceLoan(querykey, loanAppId, variables);
};

export const mkLoanPermission = (
  loanapp_id: string,
  names: string[],
  accountId: string,
  options: any,
  metadata: any
): SchemaLoanPermission[] => {
  const permissions: SchemaLoanPermission[] = map((name: string) => {
    return {
      name,
      type_meta: {
        api_version: 'v1',
        kind: "LoanPermission"
      },
      object_meta: {
        name,
        account: accountId,
        labels: {},
        annotations: {},
        owner_references: [
          {
            kind: KIND_LOANAPP,
            api_version: 'v1',
            name: loanapp_id,
            uid: loanapp_id,
            block_owner_deletion: false,
          },
        ],
        namespace: getNamespaceName(),
      },
      loanapp_id,
      options,
      metadata,
    };
  }, names)

  return permissions
}


export const useLoanPermissionHook = () => {
  const [permisionDataPost] = applicationUseMutation(
    CREATE_LOAN_PERMISSION,
    {}
  );
  const [permisionDataDelete] = applicationUseMutation(
    DELETE_LOAN_PERMISSION_LOAN_ID_AND_NAME,
    {}
  );
  const onCreateLoanPermissions = (
    loanapp_id: string,
    names: string[],
    accountId: string,
    options: any,
    metadata: any
  ) => {

    const permissions = mkLoanPermission(
      loanapp_id,
      names,
      accountId,
      options,
      metadata
    )

    return permisionDataPost({
      variables: {
        loanPermissions: permissions
      }
    }).then();
  };

  const onDeleteLoanPermissions = (
    loanAppId: string,
    accountId: string,
    loanPermissionNames: string[],
  ) => {
    const variables = {
      loanAppId,
      objectMeta: compose(
                when(
                  () => isNotEmpty(accountId),
                 set(lensPath(['account']), accountId)
               )
              )({
                namespace: getNamespaceName()
              }), 
      loanPermissionNames
    }
    return permisionDataDelete({ variables })
  }


  return {
    onCreateLoanPermissions,
    onDeleteLoanPermissions
  };
};


export const useNamespaceProducts = () => {
  const queryClient = useQueryClient();
  const { accountRoles } = useAccounts();
  const gQueryBuilder = getAccountRolePermissionsQueryBuilder(accountRoles)
  const queriyKeys = gQueryBuilder.getQueryNames()
  const variables = gQueryBuilder.getQueryVariables()
  const data = queryClient.getQueryData(
    [join(':', queriyKeys), variables]
  );
  const loanProducts = pathOr([], ['loan_products'], data)
  return loanProducts
}


const getAccountRolePermissionsQueryBuilder = (accountRoles: any, accountId?: string) => {
  const namespace = getNamespaceName()
  const productsQuery: QueryData = {
    name: NAMESPACE_PERMISSIONS_LOAN_PRODUCTS,
    query: GET_LOAN_PRODUCTS_BY_NAMESPACE,
    variables: {
      objectMeta: {
        namespace
      }
    }
  }

  const branchesQuery: QueryData = {
    name: NAMESPACE_BRANCHES,
    query: GET_BRANCHES_SHORT_BY_NAMESPACE,
    variables: {
      objectMeta: {
        namespace
      }
    }
  }

  const permissionTemplates: QueryData = {
    name: GLOBAL_PERMISSION_TEMPLATES,
    query: GQL_GLOBAL_PERMISSION_TEMPLATES,
    variables: {}
  }

  const permissionsQuery: QueryData = {
    name: AUTHENTICATED_USER_PERMISSIONS,
    query: GET_PERMISSION_BY_ROLE,
    variables: {
      roles: accountRoles
    }
  }

  const podQuery: QueryData = {
    name: USER_POD_CONFIGS,
    query: GET_POD_CONFIG_BY_EMAIL,
    variables: {
      objectMeta: {
        namespace
      },
      assignees: [{
        email: authenticatedUser.accountEmail.get()
      }]
    }
  }

  const teamQuery: QueryData = {
    name: USER_TEAMS,
    query: TEAMS_LIST_BY_ACCOUNTS,
    variables: {
      objectMeta: {
        namespace
      },
      accountIds: [accountId]
    }
  }

  const varMapsQuery: QueryData = {
    name: 'namespace_varmaps',
    query: GQL_VARMAPS_SHORT_BY_NAMESPACE,
    tableName: 'varmaps',
    hasConditionalExp: true,
    variables: {
      objectConditions: [
        {
          "object_meta": {
            "_contains": { namespace }
          }
        },
        {
          "object_meta": {
            "_contains": {
              namespace: getGlobalNamespace(),
              labels: { [buildLabel("ui_general")]: "true" }
            }
          }
        }
      ]
    }
  }

  const varMapsApplicant: QueryData = {
    name: 'namespace_varmaps',
    query: GQL_VARMAPS_SHORT_BY_NAMESPACE,
    tableName: 'varmaps',
    hasConditionalExp: true,
    variables: {
      objectConditions: [
        {
          "object_meta": {
            "_contains": {
              namespace,
              labels: { [buildLabel("ui_general")]: "true" }
            }
          }
        },
        {
          "object_meta": {
            "_contains": {
              namespace: getGlobalNamespace(),
              labels: { [buildLabel("ui_general")]: "true" }
            }
          }
        }
      ]
    }
  }

  const documentAccessControl: QueryData = {
    name: AUTHENTICATED_USER_DOCUMENT_ACCESS_CONTROLS,
    query: GET_DOCUMENT_ACCESS_CONTROLS,
    variables: {
      objectMeta: {
        namespace
      },
      roles: accountRoles
    }
  }

  const globalDocumentArtifactMinimal: QueryData = {
    name: AUTHENTICATED_USER_DOCUMENT_ARTIFACTS_MINIMAL,
    query: GQL_GLOBAL_DOCUMENT_ARTIFACTS_MINIMAL,
    variables: {}
  }

  const queries = [productsQuery]

  const isAuthenticatedBanker = isNotEmpty(authenticatedUser.accountEmail.get()) &&
    isNotEmpty(accountRoles) &&
    not(includes(USER_ONBOARD_APPLICANT, accountRoles))


  if (isNotEmpty(accountRoles)) {
    queries.push(permissionsQuery)
  }

  if (isNotEmpty(authenticatedUser.accountEmail.get()) && isNotEmpty(accountRoles)) {
    queries.push(documentAccessControl)
    queries.push(globalDocumentArtifactMinimal)
    !isAuthenticatedBanker && queries.push(varMapsApplicant)
  }

  if (isAuthenticatedBanker) {
    queries.push(podQuery)
    queries.push(permissionTemplates)
    isNotEmpty(accountId) && queries.push(teamQuery)
    queries.push(varMapsQuery)

  }

  if (equals('true', siteSettings.generalEnableBranch()), isAuthenticatedBanker) {
    queries.push(branchesQuery)
  }


  return new GQLQueryBuilder(queries, 'getBaseAuthenticatedDataOfRoles')
}


export const useAccountRolePermissions = (accountRoles: any, accountId?: string) => {

  const gQueryBuilder = getAccountRolePermissionsQueryBuilder(accountRoles, accountId)

  return useOneTimeFetch(gQueryBuilder)
}


